import React, { FC, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KButton, KSpan, KTooltip, KInput } from "kahuna-base-react-components"
import { handleAuthorizedRequest, users, youtubeVideos } from "../../../../../apis"
import { mapListToCamelCase, mapListToSnakeCase } from "../../../../../utility"
import { MissingYoutubeVideo, User, YoutubeVideo } from "../../../../../types"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
//@ts-ignore
import YoutubeIcon from "../../../../../assets/logo-icons/youtube-logo.png"
//@ts-ignore
import YoutubeSvg from "../../../../../assets/release-platform-icons/youtube.svg"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"

type AdminMissingYoutubeVideosProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const AdminMissingYoutubeVideos: FC<AdminMissingYoutubeVideosProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [missingYoutubeVideos, setMissingYoutubeVideos] = useState<MissingYoutubeVideo[]>([])
  const [date, setDate] = useState<string>("")
  const [timeoutId, setTimeoutId] = useState(null)
  const [searchUserOptions, setSearchUserOptions] = useState<KSelectOption[]>([])
  const [searchText, setSearchText] = useState<string>("")

  const [groupedVideos, setGroupedVideos] = useState<Record<string, MissingYoutubeVideo[]>>({})
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0)
  const [labels, setLabels] = useState<string[]>([])

  // After fetching videos
  const fetchMissingYoutubeVideos = async () => {
    setLoading(true)
    youtubeVideos(`/missing_bmv/?date=${date}`, "GET").then((resp) => {
      if (resp.data.results) {
        const data: MissingYoutubeVideo[] = mapListToCamelCase(resp.data.results)
        setMissingYoutubeVideos(data)

        // Group by label
        const grouped: Record<string, MissingYoutubeVideo[]> = {}
        data.forEach((video) => {
          if (!grouped[video.label]) grouped[video.label] = []
          grouped[video.label].push(video)
        })

        const labelKeys = Object.keys(grouped).sort()
        setGroupedVideos(grouped)
        setLabels(labelKeys)
        setCurrentGroupIndex(0)
      }
      setLoading(false)
    })
  }

  // Skip to next label group
  const onSkip = () => {
    setSearchText("")
    setSearchUserOptions([])
    if (currentGroupIndex < labels.length - 1) {
      setCurrentGroupIndex(currentGroupIndex + 1)
    } else {
      setMissingYoutubeVideos([])
    }
  }

  const onSearchTextChanged = async (text: string) => {
    setSearchText(text)
    if (!text) return
    setSearchUserOptions([])
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setSearchUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const onSelectUser = (user: User, videos: MissingYoutubeVideo[]) => {
    if (!window.confirm(`Are you sure you want to assing all videos to \n\n- ${user.firstName}`)) {
      return
    }

    setLoading(true)
    youtubeVideos(`/save_with_shareholder/?user_id=${user.id}`, "POST", [...mapListToSnakeCase(videos)]).then(
      (resp) => {
        if (resp.data.success) {
          setSearchText("")
          setSearchUserOptions([])
          if (currentGroupIndex < labels.length - 1) {
            setCurrentGroupIndex(currentGroupIndex + 1)
          } else {
            setMissingYoutubeVideos([])
          }
          toast.error("Success!", successFilledToastOptions)
        } else {
          toast.error(`Error saving youtube video with share holder: ${resp.data.message}`, errorFilledToastOptions)
        }
        setLoading(false)
      }
    )
  }

  const onClickYoutube = (youtubeVideo: MissingYoutubeVideo) => {
    if (youtubeVideo.isrc && youtubeVideo.isrc !== "N/A") {
      window.open(`https://www.youtube.com/results?search_query=%22${youtubeVideo.isrc}%22`, "_blank")
    } else {
      window.open(`https://www.youtube.com/watch?v=${youtubeVideo.videoId}`, "_blank")
    }
  }

  const onClickChannel = (youtubeVideo: MissingYoutubeVideo) => {
    if (youtubeVideo.channelId && youtubeVideo.channelId !== "N/A") {
      window.open(`https://www.youtube.com/channel/${youtubeVideo.channelId}`, "_blank")
    }
  }

  const renderMissingYoutubeVideos = () => {
    if (!missingYoutubeVideos.length) {
      return (
        <div>
          <KSpan text="No missing youtube videos (shareholders) found." fontSize={16} lineHeight="24px" />
        </div>
      )
    }

    const currentLabel = labels[currentGroupIndex]
    const videos = groupedVideos[currentLabel]

    return (
      <div className="p-4 border border-gray-300 rounded-lg shadow-sm bg-white w-full" style={{ width: "100%" }}>
        {/* YOUTUBE VIDEO USERS SECTION */}
        <div className="flex justify-between" style={{ width: "100%" }}>
          <div className="flex flex-col gap-2" style={{ width: "50%" }}>
            <KTitleSpan text="Search User" fontSize={16} lineHeight="24px" />

            <div className="mt-2 flex flex-col gap-2">
              <KInput
                dataTestId="search-user-input"
                placeholder={"Search user by email"}
                onChange={(text) => onSearchTextChanged(text)}
                height={24}
                value={searchText}
              />
            </div>
            {searchUserOptions.map((option) => (
              <div
                key={option.value}
                onClick={() =>
                  onSelectUser(
                    {
                      id: option.value as number,
                      firstName: option.label,
                      lastName: "",
                      email: ""
                    } as User,
                    videos
                  )
                }
                className="flex gap-1 cursor-pointer p-3 border border-gray-200 rounded hover:bg-gray-100 transition"
              >
                <KSpan text={option.label} fontSize={14} lineHeight="20px" />
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <div className="mt-2">
              <KTitleSpan text={`(${currentGroupIndex + 1}/${labels.length})`} fontSize={18} lineHeight="26px" />
            </div>
            <KButton width="100px" text="Skip" background="transparent" onClick={() => onSkip()} />
          </div>
        </div>
        {/* DIVIDER */}
        <div className="my-4 h-px w-full bg-gray-200" />

        {/* TOP SECTION */}

        {videos?.map((video) => (
          <div key={video.videoId} className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <KTitleSpan text={video.title} fontSize={18} lineHeight="26px" />
              <KSpan text={`Label: ${video.label}`} fontSize={14} lineHeight="20px" />
            </div>

            <div className="flex gap-6 flex-wrap mb-2">
              <div className="cursor-pointer items-center flex" onClick={() => onClickChannel(video)}>
                <img src={YoutubeSvg} width={20} />
              </div>
              <KSpan text={`Channel: ${video.channelName}`} fontSize={14} lineHeight="20px" />
            </div>

            <div className="flex gap-6 mt-2">
              <div className="cursor-pointer items-center flex" onClick={() => onClickYoutube(video)}>
                <img src={YoutubeIcon} width={20} />
              </div>
              <KSpan text={`Video ID: ${video.videoId}`} fontSize={14} lineHeight="20px" />
              <KSpan text={`ISRC: ${video.isrc}`} fontSize={14} lineHeight="20px" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      className="catalog flex flex-col bg-[#fff] px-8 pt-4"
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          fetchMissingYoutubeVideos()
        }
      }}
    >
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"Missing BMV Youtube Videos"} />
          </div>
        </KTooltip>
        <div className="flex gap-4">
          <KInput
            placeholder="Enter Date (YYYYMM). Ex: 202601"
            onChange={(text) => setDate(text)}
            value={date}
            width={280}
          />
          <KButton
            text="Apply"
            onClick={() => fetchMissingYoutubeVideos()}
            background="black"
            textColor="white"
            disabled={date.length !== 6}
            width="120px"
          />
        </div>
      </div>

      {renderMissingYoutubeVideos()}
    </div>
  )
}

export default AdminMissingYoutubeVideos
